import { parseMultiselectValue, visibleFlowOptions } from "@repo/shared";
import type { FlowFieldOption } from "@repo/shared/types";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { DatePicker } from "@/components/ui/date-picker";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { optionRowClass } from "@/components/ui/option-row";
import { Textarea } from "@/components/ui/textarea";
import { ApiError } from "@/lib/api/_client";
import {
  usePublicFieldValues,
  useSavePublicFieldValue,
} from "@/lib/hooks/use-public-flow/use-custom-field-values";
import { useT } from "@/lib/i18n/use-t";
import { usePublicFlowActions } from "../public-flow-context";
import { T } from "./field-renderer.i18n";
import type { FlowField } from "./types";

type PublicFieldRendererProps = {
  field: FlowField;
  /** Active goal id — used to scope a `scope: "goal"` field's value per goal. */
  goalId: string | null;
  /** Active portfolio id — used to scope a `scope: "portfolio"` field's value per portfolio. */
  portfolioId?: string | null;
  isCompleted: boolean;
  /** Follow-up C P3 — engine flagged this field as a missing required item. */
  hasValidationError?: boolean;
  /** When false, suppress the label (the caller renders it). Default true. */
  showLabel?: boolean;
};

export function FieldRenderer({
  field,
  goalId,
  portfolioId,
  isCompleted,
  hasValidationError = false,
  showLabel = true,
}: PublicFieldRendererProps) {
  const t = useT(T);
  const actions = usePublicFlowActions();
  const { fieldValues } = usePublicFieldValues();
  const saveField = useSavePublicFieldValue(
    `public-field-value:${field.id}:${goalId ?? ""}:${portfolioId ?? ""}`,
  );

  // Scope-aware resolution: portfolio > goal > global.
  const effectiveGoalId = field.scope === "goal" ? goalId : null;
  const effectivePortfolioId = field.scope === "portfolio" ? (portfolioId ?? null) : null;

  // Key for the optimistic conditional-visibility store.
  const fieldValueKey = effectivePortfolioId
    ? `${effectivePortfolioId}:${field.id}`
    : effectiveGoalId
      ? `${effectiveGoalId}:${field.id}`
      : field.id;

  // Resolve server value for this (field, scope) combination.
  const serverValue = (() => {
    if (effectivePortfolioId) {
      return (
        fieldValues.find(
          (v: any) => v.flowFieldId === field.id && v.portfolioId === effectivePortfolioId,
        )?.value || ""
      );
    }
    if (effectiveGoalId) {
      return (
        fieldValues.find((v) => v.flowFieldId === field.id && v.goalId === effectiveGoalId)
          ?.value || ""
      );
    }
    return (
      fieldValues.find((v: any) => v.flowFieldId === field.id && !v.goalId && !v.portfolioId)
        ?.value || ""
    );
  })();

  // Local state for responsive typing
  const [localValue, setLocalValue] = useState(serverValue);
  // Tracks whether the user has edits the server hasn't echoed back yet.
  const dirtyRef = useRef(false);

  // Adopt the server value only when the field is "clean" (no in-flight edit /
  // pending save). Otherwise the saved value echoing back through the query
  // cache would clobber characters the user is still typing — the inputs
  // "freeze" after a refresh. Once the server value catches up to the local
  // value the field is clean again, so genuine reloads and goal switches (which
  // change `serverValue`) are still adopted.
  useEffect(() => {
    if (serverValue === localValue) {
      dirtyRef.current = false;
      return;
    }
    if (dirtyRef.current || saveField.isPending) return;
    setLocalValue(serverValue);
  }, [serverValue, localValue, saveField.isPending]);

  const doSave = (value: string) => {
    if (!isCompleted) {
      saveField.mutate(
        {
          fieldId: field.id,
          value,
          goalId: effectiveGoalId ?? undefined,
          portfolioId: effectivePortfolioId ?? undefined,
        },
        {
          // QPBI-395 fix #2 — the server can now reject a value (400). Surface
          // it and resync the control (and the optimistic conditional-visibility
          // store) to the last known server state.
          onError: (err) => {
            toast.error(err instanceof ApiError ? err.message : t.saveFailed);
            dirtyRef.current = false;
            setLocalValue(serverValue);
            actions.setFieldValue(fieldValueKey, serverValue);
          },
        },
      );
    }
  };

  const handleChange = (value: string) => {
    dirtyRef.current = true;
    setLocalValue(value);
    // Update context for conditional visibility
    actions.setFieldValue(fieldValueKey, value);
  };

  const handleBlur = () => {
    if (!isCompleted) {
      doSave(localValue);
    }
  };

  const handleDropdownChange = (value: string) => {
    dirtyRef.current = true;
    setLocalValue(value);
    actions.setFieldValue(fieldValueKey, value);
    doSave(value);
  };

  // H3 (Follow-up C P3) — red border on the input + an inline "required" message.
  const errorClass = hasValidationError ? "border-destructive" : "";
  const wrapWithError = (input: React.ReactNode) =>
    hasValidationError ? (
      <div className="space-y-1">
        {input}
        <p className="text-xs text-destructive">{t.fieldRequired}</p>
      </div>
    ) : (
      input
    );

  return (
    <div className="space-y-2">
      {showLabel && (
        <Label htmlFor={field.id}>
          {field.name}
          {field.isRequired && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}

      {wrapWithError(
        field.type === "textarea" ? (
          <Textarea
            id={field.id}
            value={localValue}
            onChange={(e) => handleChange(e.target.value)}
            onBlur={handleBlur}
            required={field.isRequired}
            rows={4}
            disabled={isCompleted}
            className={errorClass}
          />
        ) : field.type === "dropdown" && field.options ? (
          <select
            id={field.id}
            value={localValue}
            onChange={(e) => handleDropdownChange(e.target.value)}
            required={field.isRequired}
            disabled={isCompleted}
            className={`w-full px-3 py-2 border border-input bg-background rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${errorClass}`}
          >
            <option value="">{t.selectOption}</option>
            {visibleFlowOptions(field.options, [localValue]).map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        ) : field.type === "radio" ? (
          <div
            className={hasValidationError ? "rounded-md border border-destructive p-2" : undefined}
          >
            {visibleFlowOptions(field.options, [localValue]).map((option) => (
              <label key={option.id} className={`${optionRowClass} cursor-pointer text-sm`}>
                <input
                  type="radio"
                  name={fieldValueKey}
                  value={option.id}
                  checked={localValue === option.id}
                  onChange={() => handleDropdownChange(option.id)}
                  disabled={isCompleted}
                  className="h-4 w-4 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                {option.label}
              </label>
            ))}
          </div>
        ) : field.type === "boolean" ? (
          <div
            className={
              hasValidationError
                ? `${optionRowClass} rounded-md border border-destructive p-2`
                : optionRowClass
            }
          >
            <input
              id={field.id}
              type="checkbox"
              checked={localValue === "true"}
              onChange={(e) => handleDropdownChange(e.target.checked ? "true" : "false")}
              disabled={isCompleted}
              className="h-4 w-4 disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        ) : field.type === "multiselect" ? (
          <MultiselectInput
            options={field.options ?? []}
            value={localValue}
            fieldValueKey={fieldValueKey}
            isCompleted={isCompleted}
            hasValidationError={hasValidationError}
            onChange={handleDropdownChange}
          />
        ) : field.type === "number" ? (
          <NumberInput
            field={field}
            value={localValue}
            isCompleted={isCompleted}
            onChange={handleChange}
            onBlur={handleBlur}
            className={errorClass}
          />
        ) : field.type === "date" ? (
          // QL-108 — DatePicker shows the tenant date format; the stored/sent
          // value stays yyyy-MM-dd. Complete dates save immediately (discrete
          // action, like dropdowns).
          <DatePicker
            id={field.id}
            value={localValue}
            onChange={handleDropdownChange}
            required={field.isRequired}
            disabled={isCompleted}
            className={errorClass}
          />
        ) : field.type === "datetime" ? (
          // QL-211 — shows the client's local time and saves UTC
          // YYYY-MM-DDTHH:mm:ssZ once both date and time are complete, on
          // blur like a text input: the native time input reports every
          // segment edit, so saving each one would store times never picked.
          <DateTimePicker
            id={field.id}
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
            required={field.isRequired}
            disabled={isCompleted}
            className={errorClass}
          />
        ) : (
          <Input
            id={field.id}
            type={field.type === "email" ? "email" : field.type === "phone" ? "tel" : "text"}
            value={localValue}
            onChange={(e) => handleChange(e.target.value)}
            onBlur={handleBlur}
            required={field.isRequired}
            disabled={isCompleted}
            className={errorClass}
          />
        ),
      )}
    </div>
  );
}

type MultiselectInputProps = {
  options: FlowFieldOption[];
  value: string;
  fieldValueKey: string;
  isCompleted: boolean;
  hasValidationError: boolean;
  onChange: (value: string) => void;
};

function MultiselectInput({
  options,
  value,
  fieldValueKey,
  isCompleted,
  hasValidationError,
  onChange,
}: MultiselectInputProps) {
  const selected = parseMultiselectValue(value);
  const visible = visibleFlowOptions(options, selected);
  const toggleOption = (optionId: string, checked: boolean) => {
    const next = checked ? [...selected, optionId] : selected.filter((id) => id !== optionId);
    // Keep stored order stable by option order; store "" when nothing is
    // selected so required-validation treats it as empty.
    const ordered = visible.filter((o) => next.includes(o.id)).map((o) => o.id);
    onChange(ordered.length > 0 ? JSON.stringify(ordered) : "");
  };
  return (
    <div className={hasValidationError ? "rounded-md border border-destructive p-2" : undefined}>
      {visible.map((option) => (
        <label key={option.id} className={`${optionRowClass} cursor-pointer text-sm`}>
          <input
            type="checkbox"
            name={fieldValueKey}
            value={option.id}
            checked={selected.includes(option.id)}
            onChange={(e) => toggleOption(option.id, e.target.checked)}
            disabled={isCompleted}
            className="h-4 w-4 disabled:opacity-50 disabled:cursor-not-allowed"
          />
          {option.label}
        </label>
      ))}
    </div>
  );
}

type NumberInputProps = {
  field: { id: string; isRequired: boolean };
  value: string;
  isCompleted: boolean;
  onChange: (value: string) => void;
  onBlur: () => void;
  className?: string;
};

function NumberInput({ field, value, isCompleted, onChange, onBlur, className }: NumberInputProps) {
  const allowedKeys = [
    "Backspace",
    "Delete",
    "Tab",
    "Escape",
    "Enter",
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
    "Home",
    "End",
  ];

  // QPBI-395 — a stored value the native number input cannot represent
  // (decimal comma "1,5", "abc") would render blank while conditional rules
  // still read it; fall back to a text input so the client sees what is saved.
  const numberUnrepresentable = value !== "" && Number.isNaN(Number(value));

  return (
    <Input
      id={field.id}
      type={numberUnrepresentable ? "text" : "number"}
      inputMode="decimal"
      value={value}
      onChange={(e) => {
        const newValue = e.target.value;
        // In the text fallback, allow intermediate edits so the value can be
        // corrected at all; the guard reapplies once it parses again.
        if (numberUnrepresentable || newValue === "" || !Number.isNaN(Number(newValue))) {
          onChange(newValue);
        }
      }}
      onKeyDown={(e) => {
        if (allowedKeys.includes(e.key)) return;
        if (/^[0-9.-]$/.test(e.key)) return;
        if (e.ctrlKey || e.metaKey) return;
        e.preventDefault();
      }}
      onBlur={onBlur}
      required={field.isRequired}
      disabled={isCompleted}
      className={className}
    />
  );
}
